import { ProductCatalogType } from '@/lib/product-catalog-type'
import type { ProductCatalogType as ProductCatalogTypeValue } from '@/lib/api/api.gen'
import type { InventoryProduct, InventoryVariant } from './inventory.types'

export const INVENTORY_MIN_QUANTITY = 0

type QuantityInput = string | number | null | undefined

/** An inventory form variant; `on_hand` holds the typed count. */
export interface InventoryFormVariant {
  id: number
  product_id: number
  name: string
  use_inventory: boolean
  on_hand: string
  in_stock: boolean
}

export interface InventoryFormProduct {
  id: number
  name: string
  product_catalog_type: ProductCatalogTypeValue
  is_add_on: boolean
  is_add_on_only: boolean
  variants: InventoryFormVariant[]
}

export interface InventoryFormValues {
  products: InventoryFormProduct[]
}

/**
 * What `buildInventoryUpdatePayload` sends. The inventory screen saves only after changes, so the
 * lists `UpdateInventoryInput` requires to be non-empty have entries.
 */
export interface InventoryUpdatePayload {
  products: {
    id: number
    variants: { id: number; use_inventory: boolean; on_hand: number | null }[]
  }[]
}

/** The variant fields the tracking helpers read; saved inventory and form values both have them. */
interface TrackingVariant {
  name?: string
  use_inventory?: boolean
  on_hand?: QuantityInput
}

interface TrackingProduct {
  name?: string
  product_catalog_type?: string | null
  is_add_on?: boolean
  is_add_on_only?: boolean
  variants?: TrackingVariant[]
}

/** The inventory list's product type filter. */
export type InventoryProductTypeFilter = 'all' | 'products' | 'add-ons'

export function parseInventoryQuantity(value: QuantityInput): number | null {
  if (value === '' || value === null || typeof value === 'undefined') {
    return null
  }

  if (typeof value === 'number') {
    return Number.isSafeInteger(value) ? Math.max(INVENTORY_MIN_QUANTITY, value) : null
  }

  const normalizedValue = String(value).trim()

  if (!/^-?\d+$/.test(normalizedValue)) {
    return null
  }

  if (normalizedValue.startsWith('-')) {
    return INVENTORY_MIN_QUANTITY
  }

  const parsed = Number(normalizedValue)

  return Number.isSafeInteger(parsed) ? Math.max(INVENTORY_MIN_QUANTITY, parsed) : null
}

/** Keeps a typed count to whole numbers of at least zero; anything else keeps the previous value. */
export function sanitizeInventoryQuantityInput(
  value: QuantityInput,
  previousValue: QuantityInput = ''
): string {
  const normalizedValue = String(value ?? '').trim()
  const fallbackValue =
    previousValue === null || typeof previousValue === 'undefined' ? '' : String(previousValue)

  if (normalizedValue === '') {
    return ''
  }

  if (!/^-?\d+$/.test(normalizedValue)) {
    return fallbackValue
  }

  const parsedQuantity = parseInventoryQuantity(normalizedValue)

  return parsedQuantity === null ? fallbackValue : String(parsedQuantity)
}

/** Normalization retains an omitted stock flag; the current API resource requires it. */
type InventoryNormalizationProduct = Pick<
  InventoryProduct,
  'id' | 'name' | 'product_catalog_type' | 'is_add_on' | 'is_add_on_only'
> & {
  variants: (Omit<InventoryVariant, 'in_stock'> & {
    in_stock?: InventoryVariant['in_stock']
  })[]
}

type NormalizedInventoryValues = {
  products: (Omit<InventoryFormProduct, 'variants'> & {
    variants: (Omit<InventoryFormVariant, 'in_stock'> & {
      in_stock: InventoryVariant['in_stock'] | undefined
    })[]
  })[]
}

export function normalizeInventoryFormValues(products?: InventoryProduct[]): InventoryFormValues
export function normalizeInventoryFormValues(
  products: InventoryNormalizationProduct[]
): NormalizedInventoryValues
export function normalizeInventoryFormValues(
  products: InventoryNormalizationProduct[] = []
): NormalizedInventoryValues {
  return {
    products: products.map((product) => ({
      id: product.id,
      name: product.name,
      product_catalog_type: product.product_catalog_type ?? ProductCatalogType.Standard,
      is_add_on: Boolean(product.is_add_on),
      is_add_on_only: Boolean(product.is_add_on_only),
      variants: (product.variants ?? []).map((variant) => ({
        id: variant.id,
        product_id: variant.product_id,
        name: variant.name,
        use_inventory: Boolean(variant.use_inventory),
        on_hand:
          variant.on_hand === null || typeof variant.on_hand === 'undefined'
            ? ''
            : String(variant.on_hand),
        in_stock: variant.in_stock,
      })),
    })),
  }
}

/** The editable fields the bulk request builder reads before its existing numeric-id coercion. */
type InventoryUpdateDraftProduct = {
  id: InventoryFormProduct['id'] | string
  variants?: (Pick<InventoryFormVariant, 'use_inventory' | 'on_hand'> & {
    id: InventoryFormVariant['id'] | string
  })[]
}

export function buildInventoryUpdatePayload(
  values: { products?: InventoryUpdateDraftProduct[] } = {}
): InventoryUpdatePayload {
  return {
    products: (values.products ?? []).map((product) => ({
      id: Number(product.id),
      variants: (product.variants ?? []).map((variant) => ({
        id: Number(variant.id),
        use_inventory: Boolean(variant.use_inventory),
        on_hand: variant.use_inventory ? parseInventoryQuantity(variant.on_hand) : null,
      })),
    })),
  }
}

/** Laravel names errors `products.0.variants.1.on_hand`; the form names them `products[0]...`. */
export function normalizeInventoryFieldErrors(
  errors: Record<string, string[] | string> | null | undefined
): Record<string, string[]> | null | undefined {
  if (!errors || typeof errors !== 'object') {
    return errors
  }

  const normalizedErrors: Record<string, string[]> = {}

  for (const [key, value] of Object.entries(errors)) {
    const messages = Array.isArray(value) ? value.map(String) : [String(value)]

    normalizedErrors[key.replace(/\.(\d+)(?=\.|$)/g, '[$1]')] = messages
  }

  return normalizedErrors
}

export function getTrackedVariantCount(product: TrackingProduct = {}): number {
  return (product.variants ?? []).filter((variant) => variant.use_inventory).length
}

export function getTrackedProductOnHandTotal(product: TrackingProduct = {}): number {
  return (product.variants ?? []).reduce((total, variant) => {
    if (!variant.use_inventory) {
      return total
    }

    const quantity = parseInventoryQuantity(variant.on_hand)

    return total + (quantity === null ? 0 : quantity)
  }, 0)
}

/** True when every variant is tracked, `indeterminate` when only some are. */
export function getProductTrackAllState(product: TrackingProduct = {}): boolean | 'indeterminate' {
  const variantCount = (product.variants ?? []).length
  const trackedVariantCount = getTrackedVariantCount(product)

  if (!variantCount || trackedVariantCount === 0) {
    return false
  }

  if (trackedVariantCount === variantCount) {
    return true
  }

  return 'indeterminate'
}

export function isTrackedVariantOutOfStock(variant: TrackingVariant = {}) {
  return variant.use_inventory && parseInventoryQuantity(variant.on_hand) === 0
}

export function isTrackedProductOutOfStock(product: TrackingProduct = {}): boolean {
  const trackedVariants = (product.variants ?? []).filter((variant) => variant.use_inventory)

  return trackedVariants.length > 0 && trackedVariants.every(isTrackedVariantOutOfStock)
}

/** A variant the florist just stopped tracking, which stays listed until the change is saved. */
export function isPendingUntrackedVariant(
  variant: TrackingVariant = {},
  defaultVariant: TrackingVariant | null = null
): boolean {
  return !variant.use_inventory && Boolean(defaultVariant?.use_inventory)
}

export function inventoryProductMatchesType(
  product: TrackingProduct = {},
  productType: InventoryProductTypeFilter = 'all'
): boolean {
  if (product.product_catalog_type === ProductCatalogType.SubscriptionPackage) {
    return false
  }

  if (productType === 'products') {
    return !product.is_add_on_only
  }

  if (productType === 'add-ons') {
    return Boolean(product.is_add_on)
  }

  return true
}

interface VisibleInventoryProductOptions {
  query?: string
  productType?: InventoryProductTypeFilter
  showUntracked?: boolean
  /** The product as loaded, before unsaved changes. */
  defaultProduct?: InventoryFormProduct | null
}

export interface VisibleInventoryVariant extends InventoryFormVariant {
  variantIndex: number
  isPendingUntracked: boolean
}

/** A listed product, with its index in the form and the variants the filters keep. */
export interface VisibleInventoryProduct extends InventoryFormProduct {
  productIndex: number | null
  visibleVariants: VisibleInventoryVariant[]
}

interface VisibleInventoryProductView extends TrackingProduct {
  productIndex: number | null
  visibleVariants: (TrackingVariant & {
    variantIndex: number
    isPendingUntracked: boolean
  })[]
}

type VisibleInventoryReadOptions = Omit<VisibleInventoryProductOptions, 'defaultProduct'> & {
  defaultProduct?: Pick<TrackingProduct, 'variants'> | null
}

export function getVisibleInventoryProduct(
  product: InventoryFormProduct | null | undefined,
  options?: VisibleInventoryProductOptions,
  productIndex?: number | null
): VisibleInventoryProduct | null
export function getVisibleInventoryProduct(
  product: TrackingProduct | null | undefined,
  options?: VisibleInventoryReadOptions,
  productIndex?: number | null
): VisibleInventoryProductView | null
export function getVisibleInventoryProduct(
  product: TrackingProduct | null | undefined,
  {
    query = '',
    productType = 'all',
    showUntracked = false,
    defaultProduct = null,
  }: VisibleInventoryReadOptions = {},
  productIndex: number | null = null
): VisibleInventoryProductView | null {
  if (!product) {
    return null
  }

  if (!inventoryProductMatchesType(product, productType)) {
    return null
  }

  const normalizedQuery = query.trim().toLowerCase()
  const indexedVariants = (product?.variants ?? []).map((variant, variantIndex) => ({
    ...variant,
    variantIndex,
    isPendingUntracked: isPendingUntrackedVariant(
      variant,
      defaultProduct?.variants?.[variantIndex] ?? null
    ),
  }))
  const trackingFilteredVariants = indexedVariants.filter(
    (variant) => showUntracked || variant.use_inventory || variant.isPendingUntracked
  )

  if (!trackingFilteredVariants.length) {
    return null
  }

  const productMatches = product?.name?.toLowerCase().includes(normalizedQuery)
  const visibleVariants = !normalizedQuery
    ? trackingFilteredVariants
    : productMatches
      ? trackingFilteredVariants
      : trackingFilteredVariants.filter((variant) =>
          variant.name?.toLowerCase().includes(normalizedQuery)
        )

  if (!visibleVariants.length) {
    return null
  }

  return {
    ...product,
    productIndex,
    visibleVariants,
  }
}

export function getVisibleInventoryProductCount(
  products: InventoryFormProduct[] = [],
  {
    query = '',
    productType = 'all',
    showUntracked = false,
    defaultProducts = [],
  }: Omit<VisibleInventoryProductOptions, 'defaultProduct'> & {
    defaultProducts?: InventoryFormProduct[] | null
  } = {}
): number {
  return products.reduce((count, product, productIndex) => {
    return (
      count +
      (getVisibleInventoryProduct(
        product,
        {
          query,
          productType,
          showUntracked,
          defaultProduct: defaultProducts?.[productIndex] ?? null,
        },
        productIndex
      )
        ? 1
        : 0)
    )
  }, 0)
}
